import { useState, useEffect, useRef } from 'react'

function getDaylightSecondsToday() {
  const now = new Date()
  const sunrise = new Date(now); sunrise.setHours(6, 0, 0, 0)
  const sunset  = new Date(now); sunset.setHours(18, 0, 0, 0)
  if (now < sunrise) return 0
  if (now > sunset)  return (sunset - sunrise) / 1000
  return (now - sunrise) / 1000
}

function isDaylight() {
  const h = new Date().getHours()
  return h >= 6 && h < 18
}

const DEFAULT_CFG = {
  baseKw: 5000,
  baseUnits: 180000000,
  baseDate: '2025-01-01',
  kwPerDay: 200,
  unitsPerSecondDaylight: 12,
  reasons: [],
}

function getBaseStats(cfg) {
  const c = { ...DEFAULT_CFG, ...cfg }
  const now  = new Date()
  const base = new Date(c.baseDate)
  if (isNaN(base.getTime())) return { kw: c.baseKw, units: c.baseUnits }
  const totalDays = (now - base) / 86400000
  const kw    = c.baseKw + totalDays * c.kwPerDay
  const units = c.baseUnits + (Math.floor(totalDays) * 43200 + getDaylightSecondsToday()) * c.unitsPerSecondDaylight
  return { kw, units }
}

function formatKw(n) {
  if (n >= 1000) return (n / 1000).toFixed(1) + ' MW'
  return Math.round(n).toLocaleString('en-IN') + ' kW'
}

function formatUnits(n) {
  return Math.floor(n / 1000).toLocaleString('en-IN')
}

export const WhyChooseUs = (props) => {
  // Use a stable ref for config so effects don't re-run on every render
  const cfgRef = useRef(DEFAULT_CFG)

  const [kw,           setKw]           = useState(DEFAULT_CFG.baseKw)
  const [displayUnits, setDisplayUnits] = useState(DEFAULT_CFG.baseUnits)

  // Accumulator ref — never triggers re-render itself
  const unitsAccRef = useRef(DEFAULT_CFG.baseUnits)
  const rafRef      = useRef(null)
  const lastTsRef   = useRef(null)
  const lastRenderTs = useRef(null)

  // Seed from real data once it arrives (runs once when props.data becomes truthy)
  useEffect(() => {
    if (!props.data || !props.data.baseDate) return
    cfgRef.current = { ...DEFAULT_CFG, ...props.data }
    const base = getBaseStats(cfgRef.current)
    unitsAccRef.current = base.units
    setKw(base.kw)
    setDisplayUnits(base.units)
  }, [props.data])

  // RAF loop — runs once on mount, reads from cfgRef so never needs to restart
  useEffect(() => {
    function tick(ts) {
      const rate = cfgRef.current.unitsPerSecondDaylight || DEFAULT_CFG.unitsPerSecondDaylight

      if (lastTsRef.current !== null && isDaylight()) {
        const delta = (ts - lastTsRef.current) / 1000
        unitsAccRef.current += delta * rate

        // Commit to React state only every 100 ms — avoids 60fps render storm
        if (lastRenderTs.current === null || ts - lastRenderTs.current >= 100) {
          setDisplayUnits(unitsAccRef.current)
          lastRenderTs.current = ts
        }
      }
      lastTsRef.current = ts
      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, []) // ← empty deps: starts once on mount, reads live config via cfgRef

  // kW grows slowly — refresh every 60 s
  useEffect(() => {
    const id = setInterval(() => setKw(getBaseStats(cfgRef.current).kw), 60000)
    return () => clearInterval(id)
  }, [])

  const reasons = (props.data && props.data.reasons) ? props.data.reasons : DEFAULT_CFG.reasons

  return (
    <div id='why-us' className='text-center'>
      <div className='container'>
        <div className='row'>
          <div className='col-md-10 col-md-offset-1 section-title'>
            <h2>Why Choose NewGen Solar?</h2>
            <p>Real numbers, live on every sunlit second. Our fleet never stops generating.</p>
          </div>
        </div>

        {/* Live counters */}
        <div className='row why-counters'>
          <div className='col-md-6 col-sm-6 col-xs-12'>
            <div className='counter-card'>
              <i className='fa fa-sun-o counter-icon'></i>
              <div className='counter-number'>{formatKw(kw)}</div>
              <p className='counter-label'>Total Solar Capacity Installed</p>
            </div>
          </div>
          <div className='col-md-6 col-sm-6 col-xs-12'>
            <div className='counter-card counter-card-alt'>
              <i className='fa fa-bolt counter-icon'></i>
              <div className='counter-number'>
                {formatUnits(displayUnits)} <span className='counter-unit'>K kWh</span>
              </div>
              <p className='counter-label'>
                Clean Units Generated&nbsp;
                <span className='daylight-note'>
                  {isDaylight() ? '⚡ live' : '(resumes at sunrise)'}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Reason cards */}
        <div className='row flex-card-row' style={{ marginTop: '40px' }}>
          {reasons.map((r, i) => (
            <div key={i} className='col-md-3 col-sm-6 col-xs-12 flex-card-col'>
              <div className='reason-card'>
                <i className={r.icon + ' reason-icon'}></i>
                <h3>{r.title}</h3>
                <p>{r.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Wave into About (dark navy) */}
      <div className='why-us-wave'>
        <svg viewBox='0 0 1440 70' preserveAspectRatio='none' xmlns='http://www.w3.org/2000/svg'>
          <path d='M0,0 C360,70 1080,0 1440,50 L1440,70 L0,70 Z' fill='#0f1b3d' />
        </svg>
      </div>
    </div>
  )
}
